import {useState, useMemo} from 'react'

export interface UsePaginationReturn<T> {
  paginatedData: T[]
  currentPage: number
  totalPages: number
  itemsPerPage: number
  totalItems: number
  nextPage: () => void
  prevPage: () => void
  goToPage: (page: number) => void
  setItemsPerPage: (items: number) => void
  isFirstPage: boolean
  isLastPage: boolean
}

/**
 * Hook customizado para gerenciar paginação client-side
 * @param data - Array de dados a paginar
 * @param initialItemsPerPage - Quantidade inicial de itens por página (padrão: 5)
 * @returns Objeto com dados paginados e métodos de navegação
 */
export const usePagination = <T,>(
  data: T[],
  initialItemsPerPage: number = 5
): UsePaginationReturn<T> => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(initialItemsPerPage)

  // Calcular total de páginas
  const totalPages = useMemo(() => {
    return Math.ceil(data.length / itemsPerPage)
  }, [data.length, itemsPerPage])

  // Obter dados paginados
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return data.slice(startIndex, endIndex)
  }, [data, currentPage, itemsPerPage])

  // Navegar para próxima página
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  // Navegar para página anterior
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1)
    }
  }

  // Ir para página específica
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  // Alterar quantidade de itens por página
  const handleSetItemsPerPage = (items: number) => {
    setItemsPerPage(items)
    // Volta para página 1 ao mudar quantidade
    setCurrentPage(1)
  }

  return {
    paginatedData,
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems: data.length,
    nextPage,
    prevPage,
    goToPage,
    setItemsPerPage: handleSetItemsPerPage,
    isFirstPage: currentPage === 1,
    isLastPage: currentPage === totalPages,
  }
}
